// We use this component to generate html which will be copied to email,
// so we need some rules to be ignored
import { buildQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { getTimezone } from '@calendly/timezone-tools'
import { greyLevel4, greyLevel5 } from '@calendly/ui/theme'
import moment from 'moment-timezone'
import { useSelector } from 'react-redux'

import logo from 'logo-app.png'
import { timeNotationSelector } from 'selectors/current_user'
import { formatDate, formatTime } from 'services/time_format'

export const HtmlEmail = ({ eventType, bookingUrl, timezone, groupedSpots, isOneOff, maxDays = 7 }) => {
  const { name, duration, locale, profile } = eventType

  const branding = profile?.branding
  const primaryColor = 'rgb(0, 105, 255)'
  const textColor = 'rgb(26,26,26)'
  const textColorLevel2 = 'rgb(51,51,51)'
  const calendlyLogo = logo
  const timeNotation = useSelector(timeNotationSelector)

  const calendlyUrl = () => `${window.location.protocol}//${window.location.host}`

  const bookingUrlWithParams = (url) => `${url}?${buildQuery({
    source: 'email',
    timezone
  })}`

  const changeTimeZoneBookingUrl = `${bookingUrl}?${buildQuery({
    source: 'email',
    timezone,
    timezone_picker: 'true'
  })}`

  const visibleGroupedSpots = Object.fromEntries(Object.entries(groupedSpots).sort().slice(0, maxDays))
  const renderMoreTimesLink = !isOneOff || (Object.entries(groupedSpots).length > maxDays)

  const renderTimezone = () => (
    <div>
      <b style={{ color: textColor }} />
      <span
        style={{
          fontFamily: '"Proxima Nova", sans-serif',
          fontStyle: 'normal',
          fontWeight: 'normal',
          fontSize: '14px',
          lineHeight: '17px',
          color: textColorLevel2,
        }}
      >
        {I18n.t('timezones.time_zone', { locale })}
        {': '}
        {getTimezone(timezone, { locale }).name}
        &nbsp;
      </span>
      <a
        style={{ marginLeft: '3px' }}
        href={changeTimeZoneBookingUrl}
      >
        {I18n.t('booking.invitee.change', { locale })}
      </a>
    </div>
  )

  const renderDate = (date) => {
    const formattedDate = formatDate(moment(date), I18n.t('time.formats.date_extra'), locale)

    return (
      <tr>
        <td style={{ textAlign: 'left', marginTop: '16px' }} key={date}>
          <span style={{
            fontFamily: '"Proxima Nova", sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            paddingBottom: '8px',
            color: textColor,
            fontWeight: 'bold',
          }}
          >
            {formattedDate}
            &nbsp;
          </span>
        </td>
      </tr>
    )
  }

  const renderSpot = (spot) => {
    const time = moment(spot).tz(timezone)
    const formattedTime = formatTime(time, timeNotation)
    const spotUrl = moment(spot).utc().format('YYYYMMDD[T]HHmm[Z]')
    return (
      <td
        key={spot}
        aria-label={formattedTime}
        style={{
          padding: '0',
          width: '64px',
          display: 'inline-block',
          marginRight: '4px',
          height: '22px',
          cssFloat: 'left',
          border: `1px solid ${primaryColor}`,
          borderRadius: '3px',
        }}
      >
        <table style={{ height: '21px' }}>
          <tbody>
            <tr style={{ height: '21px' }}>
              <td aria-hidden style={{ width: '7px' }} />
              <td style={{ width: '50px', textAlign: 'center', marginRight: '1px' }}>
                <a
                  href={bookingUrlWithParams(
                    `${bookingUrl}/${spotUrl}`
                  )}
                  className="spot"
                  style={{
                    fontFamily: '"Proxima Nova", sans-serif',
                    textDecoration: 'none',
                    textAlign: 'center',
                    color: primaryColor,
                    fontSize: '12px',
                    lineHeight: '16px',
                  }}
                >
                  <b
                    style={{
                      fontWeight: 'normal',
                      textDecoration: 'none',
                    }}
                  >
                    {formattedTime}
                    &nbsp;
                  </b>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    )
  }

  const renderSpotsRow = (index, spots) => (
    <tr key={index} style={{ height: '25px' }}>
      {spots.map((spot) => renderSpot(spot))}
    </tr>
  )

  const renderSpots = (spots) => {
    const spotsCount = 5
    const spotsArray = spots.slice().sort()
    const rowsCount = Math.ceil(spots.length / spotsCount)

    return (
      <table style={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}>
        <tbody>
          {Array(rowsCount).fill().map((_item, idx) => (
            renderSpotsRow(idx, spotsArray.slice(idx * spotsCount, idx * spotsCount + spotsCount))))}
        </tbody>
      </table>
    )
  }

  const renderEmailDayTable = (date, spots) => (
    <table
      key={date}
      style={{
        marginTop: '16px',
        textAlign: 'left',
        borderCollapse: 'collapse',
        borderSpacing: '0',
        width: '362px',
      }}
    >
      <tbody>
        {renderDate(date)}
        <tr><td>{renderSpots(spots)}</td></tr>
      </tbody>
    </table>
  )

  const renderEmailDays = () => Object.keys(visibleGroupedSpots).map((date) => renderEmailDayTable(date, visibleGroupedSpots[date]))

  const renderBranding = () => {
    if (!branding) return null
    return (
      <div
        style={{
          marginTop: '8px',
          color: greyLevel4,
          backgroundColor: 'white',
          borderTop: `1px solid ${greyLevel5}`,
          width: '100%',
          paddingTop: '10px',
          overflow: 'hidden',
          maxWidth: '344px',
        }}
      >
        <div style={{
          fontSize: '12px',
          cssFloat: 'right',
          textTransform: 'lowercase',
          display: 'inline-block',
        }}
        >
          {I18n.t('booking.invitee.powered_by', { locale })}
          <a
            href={calendlyUrl()}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              height: '16px',
              display: 'inline-block',
              verticalAlign: 'bottom',
              textDecoration: 'none',
              paddingLeft: '3px',
            }}
          >
            <img alt="Calendly" src={calendlyLogo} height="14" />
          </a>
        </div>
      </div>
    )
  }

  const renderMoreTimes = () => {
    if (!renderMoreTimesLink) return null

    return (
      <div style={{ marginTop: '13px' }}>
        <a
          className="more"
          href={bookingUrlWithParams(bookingUrl)}
          style={{
            textDecoration: 'none',
            cursor: 'pointer',
            color: primaryColor,
          }}
        >
          {I18n.t('booking.invitee.all_times_link', { locale })}
        </a>
      </div>
    )
  }

  const renderName = () => {
    if (name.length === 0) return null
    return (
      <div style={{
        fontFamily: '"Proxima Nova", sans-serif',
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '16px',
        lineHeight: '19px',
        color: textColor,
        marginTop: '15px',
      }}
      >
        {name}
      </div>
    )
  }

  const renderDuration = () => {
    if (duration.length === 0) return null

    return (
      <div style={
      {
        fontFamily: '"Proxima Nova", sans-serif',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '17px',
        color: textColorLevel2,
      }
    }
      >
        {duration}
      </div>
    )
  }

  return (
    <>
      <div
        style={{
          width: '0',
          height: '0',
          overflow: 'none',
          backgroundColor: 'white',
        }}
      >
        &zwnj;
      </div>
      <table
        data-calendly-intent="add-times-to-email"
        style={{
          width: '344px',
          maxWidth: '344px',
          fontFamily: '"Proxima Nova", sans-serif',
          fontStyle: 'normal',
          fontWeight: 'normal',
          fontSize: '16px',
          backgroundColor: 'white',
        }}
      >
        <tbody>
          <tr>
            <td>
              <div
                style={{
                  paddingBottom: '3px',
                  fontFamily: '"Proxima Nova", sans-serif',
                  fontSize: '13px',
                  color: textColor,
                  backgroundColor: 'white',
                  lineHeight: '1.4',
                  width: '344px',
                }}
              >
                {renderName()}
                {renderDuration()}
                {renderTimezone()}
                {renderEmailDays()}
                {renderMoreTimes()}
              </div>
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right' }}>{renderBranding()}</td>
          </tr>
        </tbody>
      </table>
      <p
        style={{
          backgroundColor: 'white',
          color: 'unset',
          fontSize: 'unset',
        }}
      >
        &zwnj;
      </p>
    </>
  )
}
